import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { withEntityState } from '../../../shared/store/entity-state.feature';
import { ScorecardV2, ScorecardV2Badge } from './models/scorecard-v2.model';

/** Moves the content matching the given LCID to the front of the list, keeping the rest in order. */
function preferLcid<T extends { LCID: number }>(content: T[] | null | undefined, lcid: number): T[] {
  const list = content ?? [];
  const match = list.find((c) => c.LCID === lcid);
  return match ? [match, ...list.filter((c) => c !== match)] : list;
}

/**
 * The API flattens the scorecard content for the customer's LCID, but actions and milestones
 * still carry content for every locale. This puts the content for the scorecard's LCID first,
 * so consumers can keep reading `Content[0]` as they did with V1.
 */
export function localiseScorecardV2(scorecard: ScorecardV2): Partial<ScorecardV2> {
  const milestone = scorecard.Milestone;
  return {
    Actions: (scorecard.Actions ?? []).map((action) => ({ ...action, Content: preferLcid(action.Content, scorecard.LCID) })),
    Milestone: milestone
      ? {
          ...milestone,
          Milestones: (milestone.Milestones ?? []).map((m) => ({ ...m, Content: preferLcid(m.Content, scorecard.LCID) })),
        }
      : milestone,
  };
}

/**
 * Signal based NgRx store for scorecards (v2).
 *
 * Holds the scorecard entity collection, sorted by Ordinal. The cache is valid for 60 seconds after a full fetch.
 * Also holds the badges returned by the same `scorecard/v2` response, in API order.
 */
export const ScorecardV2Store = signalStore(
  { providedIn: 'root' },
  withEntityState<ScorecardV2, 'ScorecardId'>('ScorecardId', {
    sortBy: 'Ordinal',
    cacheTtl: 60000,
    updaterFn: (scorecard) => localiseScorecardV2(scorecard),
  }),
  withState({ badges: [] as ScorecardV2Badge[] }),
  withMethods((store) => ({
    /** Replaces all badges in the store. */
    setBadges: (badges: ScorecardV2Badge[]) => patchState(store, { badges }),

    /** Updates the badge of the given scorecard, if it exists. */
    updateBadge: (scorecardId: number, patch: Partial<ScorecardV2Badge>) =>
      patchState(store, {
        badges: store.badges().map((badge) => (badge.ScorecardId === scorecardId ? { ...badge, ...patch } : badge)),
      }),
  }))
);
